import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, AlertCircle, Mail, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';

const AcademyEnrollmentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed, expired
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 10;

  const sessionId = searchParams.get('session_id');
  const tier = searchParams.get('tier');
  const isParent = tier?.startsWith('parent_');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      // No session ID, show success anyway (might be direct navigation)
      setStatus('success');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= maxPolls) {
      // Even on timeout, show success since Stripe redirect means payment likely went through
      setStatus('success');
      return;
    }

    try {
      const response = await api.get(`/academy/enrollment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(response.data);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Still pending, poll again
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollCount(prev => prev + 1);
      if (pollCount < maxPolls - 1) {
        setTimeout(pollPaymentStatus, 2000);
      } else {
        // On error after polling, still show success since Stripe redirected here
        setStatus('success');
      }
    }
  };

  const accentColor = isParent ? 'purple' : 'orange';

  return (
    <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
      <div className="container mx-auto px-6 py-16 text-center">
        {status === 'checking' && (
          <div className="max-w-md mx-auto">
            <Loader2 className={`w-16 h-16 animate-spin text-${accentColor}-600 mx-auto mb-6`} />
            <h2 className="text-2xl font-bold mb-4">Processing Your Enrollment...</h2>
            <p className="text-gray-400">Please wait while we confirm your payment.</p>
            <div className="mt-8 flex justify-center gap-1">
              {[...Array(maxPolls)].map((_, i) => (
                <div 
                  key={i}
                  className={`w-2 h-2 rounded-full ${
                    i < pollCount ? `bg-${accentColor}-600` : 'bg-gray-700'
                  }`}
                  style={{ backgroundColor: i < pollCount ? (isParent ? '#9333ea' : '#ea580c') : undefined }}
                />
              ))}
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="max-w-lg mx-auto">
            {/* Success Icon with Animation */}
            <div className="relative mb-8">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto animate-bounce"
                style={{ backgroundColor: isParent ? 'rgba(147, 51, 234, 0.2)' : 'rgba(234, 88, 12, 0.2)' }}
              >
                <CheckCircle 
                  className="w-14 h-14" 
                  style={{ color: isParent ? '#9333ea' : '#22c55e' }} 
                />
              </div>
              <Sparkles 
                className="absolute top-0 right-1/3 w-6 h-6 animate-pulse" 
                style={{ color: isParent ? '#9333ea' : '#f97316' }} 
              />
              <Sparkles 
                className="absolute bottom-0 left-1/3 w-4 h-4 animate-pulse delay-300" 
                style={{ color: isParent ? '#a855f7' : '#fb923c' }} 
              />
            </div>

            {/* Main Success Message */}
            <h1 className="text-4xl md:text-5xl font-black mb-6 tracking-tight">
              <span style={{ color: isParent ? '#9333ea' : '#22c55e' }}>CONGRATULATIONS!</span>
            </h1>
            
            <div 
              className="text-2xl md:text-3xl font-bold mb-8 p-6 rounded-lg border-2"
              style={{ 
                borderColor: isParent ? '#9333ea' : '#f97316',
                background: isParent ? 'rgba(147, 51, 234, 0.1)' : 'rgba(249, 115, 22, 0.1)'
              }}
            >
              <Mail className="w-8 h-8 mx-auto mb-4" style={{ color: isParent ? '#9333ea' : '#f97316' }} />
              <p className="text-white mb-2">Head to the starting line</p>
              <p className="text-gray-400 text-lg">(your inbox)</p>
              <p className="text-white mt-2">to begin your sprint —</p>
              <p 
                className="text-3xl font-black mt-2"
                style={{ color: isParent ? '#9333ea' : '#f97316' }}
              >
                NOW!
              </p>
            </div>

            {/* Additional Info */}
            <p className="text-gray-400 mb-8">
              {isParent 
                ? "Check your email for your Parent Academy login credentials and onboarding guide."
                : "Check your email for your Scholar Athlete Academy login credentials and your personalized 30-Day Sprint roadmap."
              }
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate(isParent ? '/academy?type=parent' : '/academy')}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800 font-bold px-6 py-3"
              >
                Back to Academy
              </Button>
              <Button
                onClick={() => navigate('/academy/high-performance')}
                className="font-bold px-6 py-3"
                style={{ 
                  backgroundColor: isParent ? '#9333ea' : '#f97316',
                  color: 'white'
                }}
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Tier Info */}
            {paymentInfo?.tier_name && (
              <p className="text-gray-500 text-sm mt-8">
                You enrolled in: <span className="text-white font-bold">{paymentInfo.tier_name}</span>
              </p>
            )}
          </div>
        )}

        {status === 'failed' && (
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-3xl font-black mb-4">
              <span className="text-red-500">Payment Failed</span>
            </h2>
            <p className="text-gray-400 mb-8">
              We couldn't process your payment. Please try again.
            </p>
            <Button 
              onClick={() => navigate(`/academy/enroll${isParent ? '?type=parent' : ''}`)}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-8 py-4"
            >
              Try Again
            </Button>
          </div>
        )}

        {status === 'expired' && (
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 rounded-full bg-yellow-600/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-yellow-500" />
            </div>
            <h2 className="text-3xl font-black mb-4">
              <span className="text-yellow-500">Session Expired</span>
            </h2>
            <p className="text-gray-400 mb-8">
              Your checkout session has expired. Please try again.
            </p>
            <Button 
              onClick={() => navigate(`/academy/enroll${isParent ? '?type=parent' : ''}`)}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-8 py-4"
            >
              Start Over
            </Button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 rounded-full bg-yellow-600/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-yellow-500" />
            </div>
            <h2 className="text-3xl font-black mb-4">
              <span className="text-yellow-500">Taking Longer Than Expected</span>
            </h2>
            <p className="text-gray-400 mb-8">
              Payment verification is taking longer than usual. If you completed payment, check your email for confirmation.
            </p>
            <div className="flex flex-col gap-4">
              <Button 
                onClick={() => navigate('/academy/high-performance')}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-8 py-4"
              >
                Go to Dashboard
              </Button>
              <Button 
                onClick={() => navigate(`/academy/enroll${isParent ? '?type=parent' : ''}`)}
                variant="outline"
                className="border-gray-600 text-gray-400 hover:bg-gray-800"
              >
                Try Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademyEnrollmentSuccess;
